/**
 * Simple Flight Tracking Hook
 * 
 * This hook only reads from DynamoDB - no direct FlightAware API calls.
 * The backend handles all FlightAware integration and updates DynamoDB.
 * Frontend just polls DynamoDB every 5 minutes.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import simpleFlightService from '../services/simpleFlightService';
import changeDetectionService from '../services/changeDetectionService';
import { FLIGHT_MONITOR_API_BASE } from '../config/environment';
import { authFetch } from '../utils/authFetch';
import { logger } from '../utils/logger';

// Helper: pick the best available time from a set of candidates
export const pickTime = (...candidates) => {
  for (const c of candidates) {
    if (c) return new Date(c);
  }
  return null;
};

// Helper: compute progress fields from raw departure/arrival data
export const computeProgress = (rawData, departureTime, arrivalTime) => {
  const now = new Date();
  const hasDeparted = !!(rawData.departure?.actual_out || rawData.departure?.actual_off || rawData.departure?.actual);

  let elapsedTime = null;
  let remainingTime = null;
  let totalFlightTime = null;

  if (departureTime && arrivalTime) {
    totalFlightTime = Math.round((arrivalTime - departureTime) / (1000 * 60));

    if (hasDeparted && rawData.departure?.actual) {
      const actualDepartureTime = new Date(rawData.departure.actual);
      elapsedTime = Math.round((now - actualDepartureTime) / (1000 * 60));
    }

    remainingTime = Math.max(0, Math.round((arrivalTime - now) / (1000 * 60)));
  }

  return { elapsedTime, remainingTime, totalFlightTime, hasDeparted };
};

// Helper: compute departure delay in minutes
export const computeDepartureDelay = (departure) => {
  if (!departure?.actual || !departure?.scheduled) return 0;
  return Math.round((new Date(departure.actual) - new Date(departure.scheduled)) / (1000 * 60));
};

// Helper function to format raw FlightAware data to enhanced structure
export const formatRawFlightData = (rawData) => {
  if (!rawData) return null;
  
  // If it already has the enhanced structure, return as-is
  if (rawData.progress && typeof rawData.progress === 'object' && rawData.progress.elapsedTime !== undefined) {
    return rawData;
  }
  
  const departureTime = pickTime(rawData.departure?.actual, rawData.departure?.estimated, rawData.departure?.scheduled);
  const arrivalTime = pickTime(rawData.arrival?.estimated, rawData.arrival?.scheduled);
  const progress = computeProgress(rawData, departureTime, arrivalTime);
  const departureDelay = computeDepartureDelay(rawData.departure);
  
  return {
    ...rawData,
    progress: {
      percent: rawData.progress || 0,
      ...progress
    },
    departure: {
      ...rawData.departure,
      delay: departureDelay
    }
  };
};

// Helper: get departure time from a flight status for comparison
export const getStatusDepartureTime = (status) => {
  if (!status) return null;
  return status.departure?.estimated || status.departure?.actual || status.departure?.scheduled || null;
};

// Helper: check if fetched trip data has been updated compared to initial snapshot
export const hasDataChanged = (updatedData, initialLastChecked, initialStatuses) => {
  const lastChecked = updatedData.flightStatusesLastChecked;
  
  // Check timestamp freshness
  if (lastChecked) {
    const timeSinceCheck = Date.now() - new Date(lastChecked).getTime();
    if (timeSinceCheck < 15000) {
      return true;
    }
    if (initialLastChecked && lastChecked !== initialLastChecked) {
      return true;
    }
  }
  
  // Check if any flight status departure times changed
  const currentStatuses = updatedData.flightStatuses || {};
  for (const [flightId, status] of Object.entries(currentStatuses)) {
    const initialTime = getStatusDepartureTime(initialStatuses[flightId]);
    const currentTime = getStatusDepartureTime(status);
    if (currentTime && currentTime !== initialTime) return true;
    if (!initialStatuses[flightId] && status) return true;
  }
  
  return false;
};

// Retry fetching trip data until we detect an update or exhaust retries
export const retryFetchUpdatedTrip = async (userId, tripId, initialLastChecked, initialStatuses) => {
  const maxRetries = 3;
  const retryDelay = 2000;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const data = await simpleFlightService.getTripWithFlightStatuses(userId, tripId);
    
    if (hasDataChanged(data, initialLastChecked, initialStatuses)) {
      return data;
    }
    
    if (attempt < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    } else {
      return data; // Use what we have on last attempt
    }
  }
  
  // Fallback (shouldn't reach here, but just in case)
  return await simpleFlightService.getTripWithFlightStatuses(userId, tripId);
};

// Extract and cross-reference flight statuses from trip data
export const extractFlightStatuses = (tripData) => {
  const statuses = {};
  const formattedStatuses = {};
  
  if (!tripData.flightStatuses) return { statuses, formattedStatuses };
  
  Object.entries(tripData.flightStatuses).forEach(([key, status]) => {
    const enhancedStatus = {
      ...status,
      source: 'dynamodb',
      lastUpdated: status.lastUpdated || tripData.flightStatusesLastChecked
    };
    
    statuses[key] = enhancedStatus;
    formattedStatuses[key] = formatRawFlightData(enhancedStatus);
    
    // Cross-reference: also store under alternate key (flightId ↔ flightNumber)
    if (tripData.flights) {
      const flight = tripData.flights.find(f => (f.flightId || f.flightNumber) === key);
      if (flight) {
        if (flight.flightId && key !== flight.flightId) {
          statuses[flight.flightId] = enhancedStatus;
          formattedStatuses[flight.flightId] = formatRawFlightData(enhancedStatus);
        }
        if (flight.flightNumber && key !== flight.flightNumber) {
          statuses[flight.flightNumber] = enhancedStatus;
          formattedStatuses[flight.flightNumber] = formatRawFlightData(enhancedStatus);
        }
      }
    }
  });
  
  return { statuses, formattedStatuses };
};

export const useSimpleFlightTracking = (userId, tripId, options = {}) => {
  const {
    enableTracking = true,
    onFlightUpdate = null,
    refreshInterval = 1800000, // 30 minutes - very conservative frequency
    autoStart = true,
    maxAttempts = null // No limit for production
  } = options;

  const [trip, setTrip] = useState(null);
  const [flightStatuses, setFlightStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const [hasUnviewedChanges, setHasUnviewedChanges] = useState(false);
  
  const intervalRef = useRef(null);
  const changeCheckIntervalRef = useRef(null);
  const requestInProgressRef = useRef(false);
  const lastRequestTimeRef = useRef(0);
  const requestQueueRef = useRef([]);

  // Remove function refs - they cause circular dependencies
  const onFlightUpdateRef = useRef(onFlightUpdate);
  const maxAttemptsRef = useRef(maxAttempts);
  const flightStatusesRef = useRef(flightStatuses);
  
  // Keep refs in sync
  useEffect(() => {
    onFlightUpdateRef.current = onFlightUpdate;
    maxAttemptsRef.current = maxAttempts;
    flightStatusesRef.current = flightStatuses;
  });

  /**
   * Calculate optimal frontend polling interval based on flight phase
   */
  const getPollingInterval = useCallback((flightStatuses) => {
    if (!flightStatuses || Object.keys(flightStatuses).length === 0) {
      return 300000; // 5 minutes default
    }
    
    let minInterval = 300000; // Start with 5 minutes
    
    for (const status of Object.values(flightStatuses)) {
      // Already arrived - stop polling
      if (status.arrival?.actual) {
        continue; // Don't factor this into minimum
      }
      
      // Landed, waiting for gate arrival - poll every 2 minutes
      if (status.arrival?.actual_on && !status.arrival?.actual) {
        return 120000; // 2 minutes - highest priority
      }
      
      // Calculate time until arrival
      const arrivalTime = status.arrival?.estimated || status.arrival?.scheduled;
      if (arrivalTime) {
        const now = Date.now();
        const arrival = new Date(arrivalTime).getTime();
        const minutesUntil = (arrival - now) / (1000 * 60);
        
        // Imminent arrival (< 10 min)
        if (minutesUntil < 10) {
          minInterval = Math.min(minInterval, 180000); // 3 minutes
        }
        // Approaching arrival (10-30 min)
        else if (minutesUntil < 30) {
          minInterval = Math.min(minInterval, 300000); // 5 minutes
        }
      }
    }
    
    return minInterval;
  }, []);

  const throttledRequest = useCallback(async (requestFn, minInterval = 2000) => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTimeRef.current;
    
    // If a request is already in progress, queue this one
    if (requestInProgressRef.current) {
      return new Promise((resolve, reject) => {
        requestQueueRef.current.push({ requestFn, resolve, reject });
      });
    }
    
    // If not enough time has passed, wait
    if (timeSinceLastRequest < minInterval) {
      const waitTime = minInterval - timeSinceLastRequest;
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    requestInProgressRef.current = true;
    lastRequestTimeRef.current = Date.now();
    
    try {
      const result = await requestFn();
      return result;
    } finally {
      requestInProgressRef.current = false;
      
      // Process queued requests
      if (requestQueueRef.current.length > 0) {
        const nextRequest = requestQueueRef.current.shift();
        if (nextRequest) {
          setTimeout(() => {
            throttledRequest(nextRequest.requestFn, minInterval)
              .then(nextRequest.resolve)
              .catch(nextRequest.reject);
          }, minInterval);
        }
      }
    }
  }, []);

  // Load trip data from DynamoDB
  const loadTripData = useCallback(async () => {
    if (!userId || !tripId) {
      logger.warn(`⚠️ [${userId}-${tripId}] Missing userId or tripId`, { userId, tripId });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get current attempt count and increment it
      setAttemptCount(prev => {
        const newCount = prev + 1;
        
        // Check attempt limit (only if maxAttempts is set)
        if (maxAttemptsRef.current && newCount > maxAttemptsRef.current) {
          // Stop tracking by clearing interval and setting state
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setIsTracking(false);
          return prev; // Don't update the count
        }
        return newCount;
      });
      
      // Use throttled request to prevent ERR_INSUFFICIENT_RESOURCES
      const tripData = await throttledRequest(async () => {
        // Add timeout to prevent hanging requests
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
        });

        const tripDataPromise = simpleFlightService.getTripWithFlightStatuses(userId, tripId);
        return await Promise.race([tripDataPromise, timeoutPromise]);
      }, 3000); // Minimum 3 seconds between requests
      
      setTrip(tripData);
      
      // Extract flight statuses
      const statuses = {};
      if (tripData.flightStatuses) {
        Object.entries(tripData.flightStatuses).forEach(([flightId, status]) => {
          statuses[flightId] = {
            ...status,
            source: 'dynamodb',
            lastUpdated: status.lastUpdated || tripData.flightStatusesLastChecked
          };
        });
      }
      
      // Format all flight statuses
      // Create a new object to ensure React detects the change
      const formattedStatuses = {};
      Object.keys(statuses).forEach(key => {
        formattedStatuses[key] = formatRawFlightData(statuses[key]);
      });
      
      // Force a new object reference to ensure React detects the change
      setFlightStatuses({ ...formattedStatuses });
      setLastUpdate(new Date());
      
      // Notify about flight updates
      if (onFlightUpdateRef.current && Object.keys(statuses).length > 0) {
        tripData.flights?.forEach(flight => {
          const status = statuses[flight.flightId || flight.flightNumber];
          if (status) {
            onFlightUpdateRef.current(flight, status);
          }
        });
      }
      
    } catch (err) {
      logger.error('❌ Error in loadTripData:', err.message);
      // Handle 404 errors (trip not found) by stopping tracking
      if (err.message.includes('404') || err.message.includes('not found') || err.message.includes('Trip not found')) {
        // Stop tracking this trip since it doesn't exist
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (changeCheckIntervalRef.current) {
          clearInterval(changeCheckIntervalRef.current);
          changeCheckIntervalRef.current = null;
        }
        setIsTracking(false);
        setError('Trip not found - tracking stopped');
        return;
      }
      
      // Silent error handling for other errors - don't spam console
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, tripId, throttledRequest]);

  // Lightweight change check (every 5 minutes)
  const checkForChanges = useCallback(async () => {
    if (!userId || !tripId) {
      return;
    }

    try {
      const changes = await throttledRequest(async () => {
        return await changeDetectionService.checkForChanges(userId);
      }, 2000); // Minimum 2 seconds between change checks
      
      const tripChanged = changes.some(c => c.tripId === tripId);
      
      if (tripChanged) {
        // Silent change detection - no logging
        setHasUnviewedChanges(true);
        // Optionally auto-refresh the data
        await loadTripData();
      }
    } catch (error) {
      // Handle 404 errors (trip not found) by stopping tracking
      if (error.message.includes('404') || error.message.includes('not found') || error.message.includes('Trip not found')) {
        // Stop tracking this trip since it doesn't exist
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (changeCheckIntervalRef.current) {
          clearInterval(changeCheckIntervalRef.current);
          changeCheckIntervalRef.current = null;
        }
        setIsTracking(false);
        setError('Trip not found - tracking stopped');
        return;
      }
      
      // Silent error handling for other errors
    }
  }, [userId, tripId, throttledRequest]);

  // Remove function refs - they cause circular dependencies

  // Start tracking (polling DynamoDB)
  const startTracking = useCallback(() => {
    if (!enableTracking || !userId || !tripId) {
      return;
    }

    // Don't start tracking if we already have an error indicating trip not found
    if (error && (error.includes('Trip not found') || error.includes('404'))) {
      return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Reset attempt count
    setAttemptCount(0);

    // Load immediately
    loadTripData();

    // Set up periodic polling
    intervalRef.current = setInterval(() => {
      loadTripData();
    }, refreshInterval);

    // Set up lightweight change checking (every 5 minutes)
    if (changeCheckIntervalRef.current) {
      clearInterval(changeCheckIntervalRef.current);
    }
    
            changeCheckIntervalRef.current = setInterval(() => {
              checkForChanges();
            }, 600000); // 10 minutes - very conservative

    setIsTracking(true);
  }, [enableTracking, userId, tripId, refreshInterval, maxAttempts, error, loadTripData, checkForChanges]);

  // Stop tracking
  const stopTracking = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (changeCheckIntervalRef.current) {
      clearInterval(changeCheckIntervalRef.current);
      changeCheckIntervalRef.current = null;
    }
    setIsTracking(false);
  }, []);

  // Trigger Lambda to check flight status from FlightAware
  const triggerFlightCheck = useCallback(async (flightId) => {
    try {
      // First, get trip to check if it's a test flight
      const tripData = await simpleFlightService.getTripWithFlightStatuses(userId, tripId);
      const flight = tripData.flights?.find(f => (f.flightId || f.flightNumber) === flightId);
      
      if (!flight) {
        logger.warn(`Flight ${flightId} not found in trip`);
        return;
      }
      
      // Determine which Lambda endpoint to call based on whether it's a test flight
      const isTestFlight = flight.isTestFlight || tripData.isTestFlight;
      // Both endpoints are on the same API Gateway, but at different paths
      // Extract base URL from FLIGHT_MONITOR_API_BASE (remove /flight-monitor)
      const baseUrl = FLIGHT_MONITOR_API_BASE.replace('/flight-monitor', '');
      const endpoint = isTestFlight 
        ? `${baseUrl}/test-flight-tracker/refresh`
        : `${FLIGHT_MONITOR_API_BASE}/refresh`;
      
      // Call Lambda endpoint to trigger flight check
      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: userId,
          tripId: tripId,
          flightId: flightId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to trigger flight check: ${response.status}`);
      }
      
      const result = await response.json();
      logger.debug(`Flight check triggered for ${flightId}:`, result);
      
      // Wait a moment for Lambda to complete and update DynamoDB
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      return result;
    } catch (error) {
      logger.error(`Error triggering flight check for ${flightId}:`, error);
      // Don't throw - allow fallback to just refreshing DynamoDB
      return null;
    }
  }, [userId, tripId]);
  
  // Manual refresh - triggers Lambda check then refreshes DynamoDB
  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get trip data first to check flights
      const tripData = await simpleFlightService.getTripWithFlightStatuses(userId, tripId);
      
      // Trigger Lambda checks for all flights in the trip
      if (tripData.flights && tripData.flights.length > 0) {
        const flightCheckPromises = tripData.flights.map(flight => {
          const flightId = flight.flightId || flight.flightNumber;
          return triggerFlightCheck(flightId);
        });
        
        // Wait for all flight checks to complete (or fail silently)
        await Promise.allSettled(flightCheckPromises);
        
        // Wait additional time for DynamoDB to update (eventual consistency)
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
      
      // Retry to get updated data from DynamoDB
      const updatedTripData = await retryFetchUpdatedTrip(
        userId, tripId,
        tripData.flightStatusesLastChecked,
        tripData.flightStatuses || {}
      );
      
      setTrip(updatedTripData);
      
      // Extract and cross-reference flight statuses
      const { statuses, formattedStatuses } = extractFlightStatuses(updatedTripData);
      
      setFlightStatuses(formattedStatuses);
      setLastUpdate(new Date());
      
      // Notify about flight updates
      if (onFlightUpdateRef.current && Object.keys(statuses).length > 0) {
        updatedTripData.flights?.forEach(flight => {
          const status = statuses[flight.flightId || flight.flightNumber];
          if (status) {
            onFlightUpdateRef.current(flight, status);
          }
        });
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, tripId, triggerFlightCheck]);

  // Get flight status for a specific flight
  // Reads from flightStatuses state to ensure it always has the latest data
  const getFlightStatus = useCallback((flightId) => {
    if (!flightId) return null;
    
    if (flightStatuses && flightStatuses[flightId]) {
      return flightStatuses[flightId];
    }
    
    if (trip?.flightStatuses) {
      const rawStatus = simpleFlightService.getFlightStatus(trip, flightId);
      if (rawStatus) {
        return formatRawFlightData(rawStatus);
      }
    }
    
    return null;
  }, [flightStatuses, trip]);

  // Get status info for debugging
  const getStatusInfo = useCallback(() => {
    if (!trip) return null;
    return simpleFlightService.getStatusInfo(trip);
  }, [trip]);

  // Auto-start tracking
  useEffect(() => {
    if (autoStart && userId && tripId && tripId.trim() !== '') {
      // Don't call startTracking from dependency - inline the logic
      if (!enableTracking || !userId || !tripId) {
        return;
      }

      // Don't start tracking if we already have an error indicating trip not found
      if (error && (error.includes('Trip not found') || error.includes('404'))) {
        return;
      }

      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }

      // Reset attempt count
      setAttemptCount(0);

      // Load immediately - call function directly
      loadTripData();

      // Set up dynamic polling based on flight status
      let currentTimeout = null;
      
      const scheduleNextPoll = () => {
        const interval = getPollingInterval(flightStatusesRef.current);
        
        if (currentTimeout) {
          clearTimeout(currentTimeout);
        }
        
        currentTimeout = setTimeout(async () => {
          await loadTripData();
          scheduleNextPoll(); // Recursively schedule next poll
        }, interval);
      };
      
      // Start dynamic polling
      scheduleNextPoll();
      
      // Store timeout reference for cleanup
      intervalRef.current = currentTimeout;

    // Set up lightweight change checking (every 10 minutes) - call function directly
    if (changeCheckIntervalRef.current) {
      clearInterval(changeCheckIntervalRef.current);
      changeCheckIntervalRef.current = null;
    }
    
    changeCheckIntervalRef.current = setInterval(() => {
      checkForChanges();
    }, 600000);

      setIsTracking(true);
    } else {
      // When autoStart becomes false, clean up
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      if (changeCheckIntervalRef.current) {
        clearInterval(changeCheckIntervalRef.current);
        changeCheckIntervalRef.current = null;
      }
      setIsTracking(false);
      setLoading(false);
    }

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      if (changeCheckIntervalRef.current) {
        clearInterval(changeCheckIntervalRef.current);
        changeCheckIntervalRef.current = null;
      }
      setIsTracking(false);
    };
  }, [autoStart, userId, tripId, enableTracking, error, getPollingInterval, loadTripData, checkForChanges]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (changeCheckIntervalRef.current) {
        clearInterval(changeCheckIntervalRef.current);
        changeCheckIntervalRef.current = null;
      }
      setIsTracking(false);
    };
  }, []);

  return {
    // Data
    trip,
    flightStatuses,
    loading,
    error,
    
    // Tracking state
    isTracking,
    lastUpdate,
    attemptCount, // DEBUG: Show attempt count
    hasUnviewedChanges,
    
    // Actions
    startTracking,
    stopTracking,
    refresh,
    loadTripData,
    
    // Utilities
    getFlightStatus,
    getStatusInfo,
    
    // Service info
    isStatusRecent: trip ? simpleFlightService.isStatusRecent(trip) : false,
    statusInfo: trip ? simpleFlightService.getStatusInfo(trip) : null
  };
};
